<template>
  <div>
    <Head title="Marcas" />

    <!-- Header & Actions -->
    <div class="flex flex-col sm:flex-row justify-between items-center mb-6 gap-4">
      <h1 class="text-3xl font-bold text-gray-800">Marcas</h1>
      
      <div class="flex flex-wrap items-center gap-3">
        <!-- Create Button -->
        <Link 
          href="/brands/create" 
          class="px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white rounded-lg flex items-center gap-2 transition-colors font-medium shadow-sm"
        >
          <i class="fa fa-plus" />
          Crear Marca
        </Link>
      </div>
    </div>

    <!-- Search Tool Bar -->
    <div class="flex flex-col sm:flex-row gap-4 mb-6">
      <div class="relative flex-1">
        <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <i class="fa fa-search text-gray-400" />
        </div>
        <input 
          v-model="form.search" 
          type="text" 
          class="block w-full pl-10 pr-3 py-2.5 bg-white border border-gray-200 rounded-lg focus:ring-orange-500 focus:border-orange-500 placeholder-gray-400 transition-colors shadow-sm" 
          placeholder="Buscar marca por nombre o código..."
        />
      </div>
      
      <div class="relative w-full sm:w-auto">
        <select 
          v-model="form.trashed" 
          class="w-full sm:w-auto px-4 py-2.5 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 focus:ring-orange-500 focus:border-orange-500 transition-colors font-medium shadow-sm appearance-none pr-10"
        >
          <option :value="null">Todas las marcas</option>
          <option value="with">Con eliminadas</option>
          <option value="only">Solo eliminadas</option>
        </select>
        <div class="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none">
          <i class="fa fa-chevron-down text-gray-400 text-xs" />
        </div>
      </div>
    </div>

    <!-- Content Table -->
    <div class="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      <!-- Desktop View -->
      <div class="overflow-x-auto">
        <table class="w-full whitespace-nowrap">
          <thead>
            <tr class="bg-gray-50/50 text-left border-b border-gray-100">
              <th class="py-4 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider">Nombre</th>
              <th class="py-4 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider">Código</th>
              <th class="py-4 px-6 font-bold text-xs text-gray-400 uppercase tracking-wider">Fecha creación</th>
              <th class="py-4 px-6" />
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-100">
            <tr 
              v-for="brand in brands.data" 
              :key="brand.id" 
              class="group hover:bg-gray-50 transition-colors"
            >
              <td class="px-6 py-4">
                <Link :href="`/brands/${brand.id}/edit`" class="flex items-center gap-2">
                  <span class="font-medium text-gray-900">{{ brand.name }}</span>
                  <span v-if="brand.deleted_at" class="px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-700">
                    Eliminado
                  </span>
                </Link>
              </td>
              <td class="px-6 py-4 text-gray-600 text-sm">
                <span v-if="brand.code" class="px-2 py-1 bg-gray-100 rounded text-xs font-mono text-gray-700 border border-gray-200">
                  {{ brand.code }}
                </span>
                <span v-else class="text-gray-400 italic">N/A</span>
              </td>
              <td class="px-6 py-4 text-gray-600 text-sm">
                <div class="flex items-center gap-2">
                  <i class="fa fa-calendar-alt text-gray-400 text-xs" />
                  {{ new Date(brand.created_at).toLocaleDateString() }}
                </div>
              </td>
              <td class="px-6 py-4 text-right">
                <Link :href="`/brands/${brand.id}/edit`" class="text-gray-400 hover:text-orange-500 transition-colors">
                  <i class="fa fa-chevron-right" />
                </Link>
              </td>
            </tr>

            <!-- Empty State -->
            <tr v-if="!brands.data.length">
              <td colspan="4" class="py-20 text-center">
                <div class="flex flex-col items-center justify-center text-gray-400">
                  <div class="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                    <i class="fa fa-search text-gray-400" /> <!-- Changed icon since 'brands' icon might not exist in simple FA set, search is safe -->
                  </div>
                  <h3 class="text-lg font-medium text-gray-900 mb-1">No hay marcas disponibles</h3>
                  <p class="text-gray-500 text-sm max-w-sm">
                    Empieza por crear una nueva marca en el sistema.
                  </p>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
    
    <div class="mt-6 flex justify-center">
      <pagination v-if="brands.links" :links="brands.links" />
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Icon from '@/Shared/Icon'
import Layout from '@/Shared/Layout'
import Pagination from '@/Shared/Pagination'
import TextInput from '@/Shared/TextInput'
import throttle from 'lodash/throttle'
import pickBy from 'lodash/pickBy'

export default {
  name: 'BrandsIndex',
  
  components: {
    Head,
    Icon,
    Link,
    Pagination,
    TextInput,
  },

  layout: Layout,

  props: {
    filters: Object,
    brands: Object,
  },

  data() {
    return {
      searchForm: false,
      form: {
        search: this.filters.search || '',
        trashed: this.filters.trashed || null,
      },
    }
  },

  watch: {
    form: {
      deep: true,
      handler: throttle(function() {
        this.$inertia.get(
          '/brands',
          pickBy(this.form),
          { preserveState: true },
        )
      }, 300),
    },
  },

  methods: {
    back() {
      window.history.back()
    },

    toggleSearch() {
      this.searchForm = !this.searchForm
      if(this.searchForm) {
        this.$nextTick(() => {
          this.$refs.searchInput?.focus()
        })
      }
    },

    reset() {
      this.form.search = ''
      this.form.trashed = null
    },
  },
}
</script>
